import axios from "axios";
import { fetchEventData } from "fetch-sse";
import { message } from "./message";
import { store } from "./Store";
import shortid from "shortid";

let token: string;
let cachedClientId = "";
const AIBASEURL = process.env.AI_SERVER_PATH || "https://ai.jianguoke.cn";
const ORDERBASEURL = process.env.ORDER_SERVER_PATH || ".";
const BASEURL = process.env.SERVER_PATH || ".";
const win = global as any;

export interface AgentToolCall {
  callId: string;
  toolName: string;
  params: any;
  actionName: string;
}

export function getClientId() {
  if (cachedClientId) return cachedClientId;
  let clientId = localStorage.getItem("clientId");
  if (!clientId) {
    // Try to get from cookie
    const match = document.cookie.match(new RegExp("(^| )clientId=([^;]+)"));
    if (match) {
      clientId = match[2];
    }
  }
  if (!clientId) {
    clientId = shortid.generate();
    localStorage.setItem("clientId", clientId);
    // Set cookie for cross-subdomain sharing
    const hostname = window.location.hostname;
    // Check if it is an IP address
    const isIp = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);
    if (!isIp && hostname !== "localhost") {
      const parts = hostname.split(".");
      if (parts.length >= 2) {
        const domain = parts.slice(-2).join(".");
        document.cookie = `clientId=${clientId}; domain=.${domain}; path=/; max-age=31536000`;
      }
    } else {
      document.cookie = `clientId=${clientId}; path=/; max-age=31536000`;
    }
  }
  cachedClientId = clientId;
  return clientId;
}

// 响应拦截器
axios.interceptors.response.use(
  (response) => {
    // 对响应数据做点什么
    return response;
  },
  (error) => {
    message.destroy();
    // 对响应错误做点什么
    if (error.response) {
      // 在这里根据状态码或其他条件向用户显示相应的提示信息
      if (error.response.status === 404) {
        message.error("请求的资源不存在！");
      } else if (error.response.status === 401) {
        message.error("未授权，请重新登录！");
        setToken("");
        store.logout();
      } else if (error.response.status === 403) {
        message.error("禁止访问！");
      } else {
        message.error(
          error.response.data?.message || "发生了一个错误，请稍后重试。"
        );
      }
      return;
    } else if (error.code !== "ERR_CANCELED") {
      if (error.config.url.endsWith("/behavior")) {
        return;
      }
      // 在这里处理网络错误的提示
      // message.error("网络错误，请检查您的网络连接并重试。");
      return;
    }
    return Promise.reject(error);
  }
);
axios.interceptors.request.use((config) => {
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-Client-Id"] = getClientId();
  return config;
});

export function getToken() {
  return token;
}

export function setToken(jwtToken: string) {
  token = jwtToken;
  win.sessionStorage.setItem("token", jwtToken);
}

export async function toolCallback(
  toolName: string,
  callId: string,
  data: any
) {
  await axios.post(`${AIBASEURL}/api/tool/${toolName}`, { callId, data });
}

export async function getOrderTypes(): Promise<
  { type: string; name: string; price: number; period?: number }[]
> {
  const res = await axios.get(`${ORDERBASEURL}/api/order/types/course`);
  return res.data;
}

export async function hasOrder(courseCode: string) {
  const res = await axios.get(`${ORDERBASEURL}/api/order/course/${courseCode}`);
  return res?.data.ok;
}

export async function refershToken(token: string) {
  const LOGINURL = `${process.env.LOGIN_SERVER_PATH}/api/refresh`;
  const res = await axios.post(LOGINURL, {
    token,
  });
  return res?.data;
}

export async function makeOrder(
  code: string,
  type: string,
  client: "h5" | "native" | "jsapi" | "app",
  payChannel: string
) {
  const res = await axios.post(`${ORDERBASEURL}/api/order/make/course`, {
    code,
    type,
    client,
    payChannel,
  });
  return res?.data;
}

export async function queryOrder(orderId: number) {
  const res = await axios.get(`${ORDERBASEURL}/api/order/query?id=${orderId}`);
  return res?.data;
}

export async function requestCancelOrder(orderId: number) {
  const res = await axios.post(`${ORDERBASEURL}/api/order/cancel`, {
    id: orderId,
  });
  return res?.data;
}

export async function queryOrderStatus(params: {
  id: number;
  ctrl?: AbortController;
  callback: (payStatus: { state: string; state_desc: string }) => void;
}) {
  let err;
  await fetchEventData(`${ORDERBASEURL}/api/order/status`, {
    method: "POST",
    signal: params.ctrl?.signal,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
      // 添加一个唯一的自定义头部，包含当前时间戳
      "X-Custom-No-Cache": new Date().getTime().toString(),
    },
    data: {
      id: params.id,
    },
    onError(error) {
      err = error;
    },
    async onMessage(ev) {
      const data = JSON.parse(ev!.data);
      params.callback(data);
    },
  });
  if (err) {
    throw new Error(err);
  }
}

export async function refershOrder() {
  const res = await axios.get(`${ORDERBASEURL}/api/order/list`);
  return res?.data;
}

export async function refundOrder(orderId: number) {
  const res = await axios.post(`${ORDERBASEURL}/api/order/refund`, {
    orderId,
  });
  return res?.data;
}

export async function getExamList() {
  const res = await axios.get(`${BASEURL}/api/exam`);
  return res?.data;
}

export async function getExamPaper(id: string) {
  const res = await axios.get(`${BASEURL}/api/exam/${id}`);
  return res?.data;
}

export async function updateExamPager(data: any) {
  const res = await axios.post(`${BASEURL}/api/exam`, data);
  return res?.data;
}

export async function deleteExamPager(id: string) {
  const res = await axios.delete(`${BASEURL}/api/exam/${id}`);
  return res?.data;
}
